import { Pause, Play, Repeat, Repeat1, RotateCcw, Shuffle, SkipBack, SkipForward } from 'lucide-react'

import type { RepeatMode } from '@/api/types'
import { usePlayer } from '@/audio/store'
import { IconButton, type IconButtonSize } from '@/components/ui'
import { cn } from '@/lib/cn'

/**
 * The transport buttons, shared by the docked bar and the full screen player so
 * the two can never drift apart. Each one reads the store itself rather than
 * taking a dozen props from two different call sites.
 */
export interface TransportButtonProps {
  size?: IconButtonSize
  className?: string
  iconClassName?: string
}

const ICON_SIZES: Record<IconButtonSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
}

export function PlayPauseButton({ size = 'md', className, iconClassName }: TransportButtonProps) {
  const isPlaying = usePlayer((state) => state.isPlaying)
  const waiting = usePlayer((state) => state.awaitingContinuation)
  const error = usePlayer((state) => state.error)
  const index = usePlayer((state) => state.index)
  const toggle = usePlayer((state) => state.toggle)
  const playAt = usePlayer((state) => state.playAt)

  const icon = cn(ICON_SIZES[size], iconClassName)
  const label = error ? 'Retry playback' : isPlaying || waiting ? 'Pause' : 'Play'

  return (
    <IconButton
      aria-label={label}
      variant="primary"
      round
      size={size}
      className={className}
      // After a failure the button becomes a retry: re-selecting the current
      // index clears the error and asks the engine to start the track again.
      onClick={() => (error ? playAt(index) : toggle())}
    >
      {error ? (
        <RotateCcw className={icon} aria-hidden="true" />
      ) : isPlaying || waiting ? (
        <Pause className={cn(icon, 'fill-current')} aria-hidden="true" />
      ) : (
        // A solid triangle sits optically left of centre in a circle.
        <Play className={cn(icon, 'translate-x-px fill-current')} aria-hidden="true" />
      )}
    </IconButton>
  )
}

export function PreviousButton({ size = 'md', className, iconClassName }: TransportButtonProps) {
  const previous = usePlayer((state) => state.previous)

  return (
    <IconButton aria-label="Previous song" size={size} className={className} onClick={previous}>
      <SkipBack className={cn(ICON_SIZES[size], 'fill-current', iconClassName)} aria-hidden="true" />
    </IconButton>
  )
}

export function NextButton({ size = 'md', className, iconClassName }: TransportButtonProps) {
  const next = usePlayer((state) => state.next)

  return (
    // `next` takes an `auto` flag; handing it the click event straight would read
    // as auto=true and trigger repeat-one.
    <IconButton aria-label="Next song" size={size} className={className} onClick={() => next(false)}>
      <SkipForward
        className={cn(ICON_SIZES[size], 'fill-current', iconClassName)}
        aria-hidden="true"
      />
    </IconButton>
  )
}

export function ShuffleButton({ size = 'sm', className, iconClassName }: TransportButtonProps) {
  const shuffle = usePlayer((state) => state.shuffle)
  const toggleShuffle = usePlayer((state) => state.toggleShuffle)

  return (
    <IconButton
      aria-label="Shuffle"
      aria-pressed={shuffle}
      size={size}
      className={cn(shuffle && 'text-accent hover:text-accent', className)}
      onClick={toggleShuffle}
    >
      <Shuffle className={cn(ICON_SIZES[size], iconClassName)} aria-hidden="true" />
    </IconButton>
  )
}

const REPEAT_LABELS: Record<RepeatMode, string> = {
  off: 'Repeat off',
  all: 'Repeat all',
  one: 'Repeat one',
}

export function RepeatButton({ size = 'sm', className, iconClassName }: TransportButtonProps) {
  const repeat = usePlayer((state) => state.repeat)
  const cycleRepeat = usePlayer((state) => state.cycleRepeat)

  const Icon = repeat === 'one' ? Repeat1 : Repeat

  return (
    <IconButton
      aria-label={REPEAT_LABELS[repeat]}
      size={size}
      className={cn(repeat !== 'off' && 'text-accent hover:text-accent', className)}
      onClick={cycleRepeat}
    >
      <Icon className={cn(ICON_SIZES[size], iconClassName)} aria-hidden="true" />
    </IconButton>
  )
}
